import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  Clock, ArrowLeft, Search, ChevronDown, ChevronUp, ChevronRight,
  CreditCard, CheckCircle, AlertCircle, Loader2, RefreshCw,
  TrendingUp, TrendingDown, Users, Wallet, History, X, Flag, Calendar
} from 'lucide-react';
import API_URL from '../config';

const OT_API = `${API_URL}/overtime`;

const fmtH = (n) => parseFloat(parseFloat(n).toFixed(2)).toString();
// Signed hours label, e.g. "+5h" / "-4h" / "0h"
const fmtSigned = (n) => {
  const v = parseFloat(n);
  if (!v) return '0h';
  return `${v > 0 ? '+' : ''}${fmtH(v)}h`;
};

// ── Instant tooltip (no OS delay) ─────────────────────────────────────────────
const Tooltip = ({ text, children }) => (
  <span className="relative group/tip inline-flex items-center gap-1">
    {children}
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/tip:opacity-100 pointer-events-none z-50 text-center leading-relaxed whitespace-normal transition-opacity duration-100">
      {text}
      <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </span>
  </span>
);

// ── Status badge for transaction type ─────────────────────────────────────────
const TypeBadge = ({ type }) => {
  if (type === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CreditCard className="w-3 h-3" /> Payé
      </span>
    );
  }
  if (type === 'opening_balance') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
        <Flag className="w-3 h-3" /> Ouverture
      </span>
    );
  }
  if (type === 'week_settlement') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        <Calendar className="w-3 h-3" /> Semaine
      </span>
    );
  }
  if (type === 'week_deficit') {
    // Legacy rows from the pre-NET model
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <TrendingDown className="w-3 h-3" /> Déficit (ancien)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
      <Clock className="w-3 h-3" /> Congé comp.
    </span>
  );
};

// Signed amount for a transaction, falling back to legacy semantics (hours_resolved
// was always a *removal* before the signed `amount` column existed).
const txAmount = (tx) => {
  if (tx.amount !== null && tx.amount !== undefined) return parseFloat(tx.amount);
  return -parseFloat(tx.hours_resolved);
};

// ── Pay Modal ─────────────────────────────────────────────────────────────────
const PayModal = ({ employee, balance, onClose, onSuccess }) => {
  const [mode, setMode] = useState('full');
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  const available = parseFloat(balance?.accumulated_hours ?? 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const toResolve = mode === 'full' ? available : parseFloat(hours);
    if (!toResolve || toResolve <= 0) { setError('Montant invalide.'); return; }
    if (toResolve > available) { setError(`Maximum disponible : ${fmtH(available)}h`); return; }

    setLoading(true);
    try {
      const res = await fetch(`${OT_API}/resolve/pay/${employee.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours_to_resolve: toResolve, note: note || undefined }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur lors du paiement');
      }
      onSuccess(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Payer des heures sup.</h2>
            <p className="text-sm text-gray-500 mt-0.5">{employee.full_name || employee.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
            <div className="text-sm text-emerald-700 font-medium">Solde net disponible</div>
            <div className="text-2xl font-bold text-emerald-600">{fmtH(available)}h</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setMode('full')}
              className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${mode === 'full' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
              Totalité<br /><span className="text-xs font-normal">{fmtH(available)}h</span>
            </button>
            <button type="button" onClick={() => { setMode('partial'); setHours(''); }}
              className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${mode === 'partial' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
              Partiel<br /><span className="text-xs font-normal">Choisir le montant</span>
            </button>
          </div>

          {mode === 'partial' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heures à payer <span className="text-gray-400">(max {fmtH(available)}h)</span>
              </label>
              <div className="relative">
                <input type="number" step="0.01" min="0.01" max={available} value={hours}
                  onChange={e => setHours(e.target.value)} placeholder="Ex: 3.5" required
                  className="w-full pl-4 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">h</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-gray-400">(optionnel)</span></label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="Ex: paiement fiche de paie février"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium hover:shadow-lg active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {loading ? 'Traitement…' : 'Confirmer le paiement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── History Drawer ────────────────────────────────────────────────────────────
const HistoryDrawer = ({ member, onClose, onBalanceUpdate }) => {
  const { employee, balance: initialBalance, openPay } = member;
  const [balance, setBalance] = useState(initialBalance);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [showSettlements, setShowSettlements] = useState(false);
  const [showPayModal, setShowPayModal] = useState(!!openPay);
  const token = localStorage.getItem('token');

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const [txRes, balRes] = await Promise.all([
        fetch(`${OT_API}/transactions/${employee.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${OT_API}/balance/${employee.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (txRes.ok) setTransactions(await txRes.json());
      if (balRes.ok) {
        const freshBal = await balRes.json();
        setBalance(freshBal);
        if (onBalanceUpdate) onBalanceUpdate(employee.id, freshBal);
      }
    } catch { /* silent */ } finally {
      setTxLoading(false);
    }
  }, [employee.id, token, onBalanceUpdate]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const accumulated = parseFloat(balance?.accumulated_hours ?? 0);
  const canPay = accumulated > 0;
  const isNoHS = employee.contract_type === 'CDI_CADRE_NO_HS';
  const netColor = accumulated > 0 ? 'text-emerald-600' : accumulated < 0 ? 'text-red-600' : 'text-gray-900';

  const settlements = transactions.filter(t => t.resolution_type === 'week_settlement');
  const notable = transactions.filter(t => t.resolution_type !== 'week_settlement');
  const visible = showSettlements ? transactions : notable;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-400 flex items-center justify-center text-white font-bold text-sm">
              {(employee.full_name || employee.email).charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-gray-900">{employee.full_name || employee.email}</div>
              <div className="text-xs text-gray-500">{employee.weekly_hours}h / semaine · {employee.contract_type}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Balance + actions */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Solde net</div>
            {isNoHS ? (
              <span className="text-sm text-slate-400 font-medium">Forfait jours</span>
            ) : (
              <span className={`text-2xl font-bold ${netColor}`}>{fmtH(accumulated)}h</span>
            )}
          </div>
          {canPay && !isNoHS && (
            <button
              onClick={() => setShowPayModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              Payer
            </button>
          )}
        </div>

        {/* Transactions */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <History className="w-3.5 h-3.5" /> Mouvements
            </span>
            <button onClick={fetchTransactions} className="p-1 hover:bg-gray-100 rounded transition-colors" title="Rafraîchir">
              <RefreshCw className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>

          {txLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">Aucun mouvement enregistré</p>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {visible.map(tx => {
                  const amt = txAmount(tx);
                  const amtColor = amt > 0 ? 'text-emerald-600' : amt < 0 ? 'text-red-600' : 'text-gray-500';
                  return (
                    <div key={tx.id} className="px-5 py-3 flex items-start gap-3">
                      <TypeBadge type={tx.resolution_type} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-semibold ${amtColor}`}>{fmtSigned(amt)}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(tx.resolved_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        {tx.week_start && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            Sem. du {new Date(tx.week_start + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </p>
                        )}
                        {tx.note && <p className="text-xs text-gray-500 mt-0.5 truncate">{tx.note}</p>}
                        {tx.leave_days_credited && (
                          <p className="text-xs text-purple-600 mt-0.5">{parseFloat(tx.leave_days_credited).toFixed(2)}j de congé crédités</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {settlements.length > 0 && (
                <button
                  onClick={() => setShowSettlements(v => !v)}
                  className="w-full flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-slate-500 hover:bg-slate-50 border-t border-gray-100 transition-colors"
                >
                  {showSettlements ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showSettlements
                    ? 'Masquer les mouvements hebdomadaires'
                    : `Voir les mouvements hebdomadaires (${settlements.length})`}
                </button>
              )}
            </>
          )}
        </div>

        {/* Modals */}
        {showPayModal && (
          <PayModal
            employee={employee}
            balance={balance}
            onClose={() => setShowPayModal(false)}
            onSuccess={(updatedBalance) => {
              setBalance(updatedBalance);
              onBalanceUpdate(employee.id, updatedBalance);
              setShowPayModal(false);
              fetchTransactions();
            }}
          />
        )}
      </div>
    </div>
  );
};

// ── Employee Own View (non-manager) ──────────────────────────────────────────
function EmployeeOwnView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await fetch(`${OT_API}/balance`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setBalance(await res.json());
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchBalance().finally(() => setLoading(false)); }, [fetchBalance]);

  const accumulated = parseFloat(balance?.accumulated_hours ?? 0);
  const isNoHS = user?.contract_type === 'CDI_CADRE_NO_HS';
  const hoursColor = accumulated > 0
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : accumulated < 0
      ? 'text-red-700 bg-red-50 border-red-200'
      : 'text-gray-700 bg-gray-50 border-gray-200';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-violet-600 mx-auto" />
          <p className="mt-3 text-gray-500">Chargement…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate(-1)}
            className="p-2 hover:bg-white hover:shadow-sm rounded-xl transition-all text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Clock className="w-6 h-6 text-amber-500" /> Mes heures supplémentaires
            </h1>
            <p className="text-sm text-gray-500">Votre solde net d'heures</p>
          </div>
          <button onClick={fetchBalance} className="p-2 hover:bg-white hover:shadow-sm rounded-xl transition-all text-gray-400">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-slate-400 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
              {(user?.full_name || user?.email || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-800 text-lg">{user?.full_name || user?.email}</div>
              {user?.weekly_hours && <div className="text-xs text-gray-500">{user.weekly_hours}h / semaine</div>}
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-lg font-bold ${hoursColor}`}>
              <Clock className="w-5 h-5" />
              {balance === null ? '—' : `${fmtH(accumulated)}h`}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            {isNoHS ? (
              <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0 text-slate-400" />
                <span>Votre contrat <strong>CDI Cadre</strong> est au forfait jours — vous ne générez pas d'heures supplémentaires.</span>
              </div>
            ) : accumulated > 0 ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <TrendingUp className="w-4 h-4 flex-shrink-0" />
                <span>Vous avez <strong>{fmtH(accumulated)}h</strong> d'heures supplémentaires. Contactez votre manager pour les régler.</span>
              </div>
            ) : accumulated < 0 ? (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
                <TrendingDown className="w-4 h-4 flex-shrink-0" />
                <span>Vous êtes en déficit de <strong>{fmtH(Math.abs(accumulated))}h</strong> par rapport à votre contrat.</span>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center">Votre compteur est à l'équilibre (0h).</p>
            )}
          </div>
        </div>

      </main>
    </div>
  );
}

// ── Manager View ───────────────────────────────────────────────────────────────
function ManagerView() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerMember, setDrawerMember] = useState(null);
  const [sortAsc, setSortAsc] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${OT_API}/team-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMembers(await res.json());
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBalanceUpdate = useCallback((userId, newBalance) => {
    setMembers(prev => prev.map(m =>
      m.user.id === userId
        ? { ...m, accumulated_hours: newBalance.accumulated_hours }
        : m
    ));
  }, []);

  // Stats
  const nets = members.map(m => parseFloat(m.accumulated_hours || 0));
  const totalNet = nets.reduce((s, n) => s + n, 0);
  const positiveTotal = nets.filter(n => n > 0).reduce((s, n) => s + n, 0);
  const creditCount = nets.filter(n => n > 0).length;
  const deficitCount = nets.filter(n => n < 0).length;

  const filtered = members.filter(m => {
    const q = search.toLowerCase();
    return (m.user.full_name || '').toLowerCase().includes(q) || (m.user.email || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const aNoHS = a.user.contract_type === 'CDI_CADRE_NO_HS';
    const bNoHS = b.user.contract_type === 'CDI_CADRE_NO_HS';
    if (aNoHS !== bNoHS) return aNoHS ? 1 : -1;
    const diff = parseFloat(a.accumulated_hours || 0) - parseFloat(b.accumulated_hours || 0);
    return sortAsc ? diff : -diff;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-violet-600 mx-auto" />
          <p className="mt-3 text-gray-500">Chargement…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-12">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-white hover:shadow-sm rounded-xl transition-all text-gray-500 flex-shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="p-2 bg-indigo-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-extrabold text-[#1A1B4B]">Heures supplémentaires</h1>
            </div>
            <p className="text-gray-500 text-sm ml-1">Solde net de l'équipe — les déficits diminuent automatiquement les heures sup.</p>
          </div>
          <button onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition-colors">
            <RefreshCw className="w-4 h-4" /> Actualiser
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <div className="text-xs text-gray-500 font-medium">Solde net total</div>
              <div className="text-2xl font-extrabold text-[#1A1B4B]">{fmtSigned(totalNet)}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <div className="text-xs text-gray-500 font-medium">Créditeurs / déficitaires</div>
              <div className="text-2xl font-extrabold text-[#1A1B4B]">{creditCount} / {deficitCount}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Wallet className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <Tooltip text="Coût estimé des heures sup. positives, majorées à 25% (les déficits ne coûtent rien).">
                <div className="text-xs text-gray-500 font-medium flex items-center gap-1">Coût estimé majoré</div>
              </Tooltip>
              <div className="text-2xl font-extrabold text-[#1A1B4B]">{positiveTotal > 0 ? `${fmtH(positiveTotal * 1.25)}h` : '0h'}</div>
              {positiveTotal > 0 && <div className="text-xs text-gray-400">majorées à 25%</div>}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un employé…"
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <th className="text-left px-4 py-3 rounded-tl-xl">Employé</th>
                <th className="text-left px-4 py-3">Contrat</th>
                <th className="px-4 py-3">
                  <Tooltip text="Solde net signé : heures faites au-delà du contrat, moins les déficits, moins ce qui a été payé/converti. Peut être négatif.">
                    <button
                      onClick={() => setSortAsc(v => !v)}
                      className="flex items-center gap-1 mx-auto hover:text-gray-800 transition-colors"
                    >
                      Heures Supp (net)
                      {sortAsc ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                  </Tooltip>
                </th>
                <th className="text-center px-4 py-3">Statut</th>
                <th className="text-right px-4 py-3">Actions</th>
                <th className="w-8 rounded-tr-xl"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p>Aucun employé trouvé</p>
                  </td>
                </tr>
              ) : sorted.map(member => {
                const accumulated = parseFloat(member.accumulated_hours || 0);
                const isNoHS = member.user.contract_type === 'CDI_CADRE_NO_HS';
                const canPay = accumulated > 0 && !isNoHS;
                const provisional = member.current_week_provisional != null
                  ? parseFloat(member.current_week_provisional) : null;
                const pendingWeeks = member.pending_validation_weeks || 0;

                const rowBorder = accumulated > 0
                  ? 'border-l-2 border-l-emerald-400'
                  : accumulated < 0
                    ? 'border-l-2 border-l-red-400'
                    : 'border-l-2 border-l-transparent';

                const avatarClass = accumulated > 0
                  ? 'bg-emerald-100 text-emerald-700'
                  : accumulated < 0
                    ? 'bg-red-100 text-red-700'
                    : isNoHS ? 'bg-slate-100 text-slate-400' : 'bg-slate-200 text-slate-500';

                const rowBg = isNoHS ? 'bg-slate-50/60' : '';
                const netTextColor = accumulated > 0 ? 'text-emerald-700' : accumulated < 0 ? 'text-red-700' : 'text-slate-400';

                return (
                  <tr
                    key={member.user.id}
                    onClick={() => setDrawerMember({
                      employee: member.user,
                      balance: { accumulated_hours: member.accumulated_hours },
                    })}
                    className={`group hover:bg-slate-50 cursor-pointer transition-colors ${rowBorder} ${rowBg}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${avatarClass}`}>
                          {(member.user.full_name || member.user.email).charAt(0).toUpperCase()}
                        </div>
                        <span className={`font-medium ${isNoHS ? 'text-slate-500' : 'text-gray-800'}`}>
                          {member.user.full_name || member.user.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {member.user.weekly_hours ? `${member.user.weekly_hours}h / sem.` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isNoHS ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                          Forfait jours
                        </span>
                      ) : (
                        <span className={`text-lg font-bold ${netTextColor}`}>{fmtH(accumulated)}h</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isNoHS ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          {provisional != null && provisional !== 0 && (
                            <Tooltip text="Net provisoire de la semaine en cours — pas encore inscrit au solde tant que la semaine n'est pas terminée et validée.">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                <Clock className="w-3 h-3" />
                                Cette sem. {fmtSigned(provisional)}
                              </span>
                            </Tooltip>
                          )}
                          {pendingWeeks > 0 && (
                            <Tooltip text="Semaines passées pas encore entièrement validées : elles ne seront inscrites au solde qu'une fois la validation terminée.">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                                <AlertCircle className="w-3 h-3" />
                                {pendingWeeks} sem. non inscrite{pendingWeeks > 1 ? 's' : ''}
                              </span>
                            </Tooltip>
                          )}
                          {(provisional == null || provisional === 0) && pendingWeeks === 0 && (
                            <span className="text-gray-300">—</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                        {canPay && (
                          <button
                            onClick={() => setDrawerMember({
                              employee: member.user,
                              balance: { accumulated_hours: member.accumulated_hours },
                              openPay: true,
                            })}
                            className="px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 transition-colors"
                          >
                            Payer
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="pl-1 pr-3 py-3">
                      <ChevronRight className={`w-4 h-4 transition-colors ${isNoHS ? 'text-slate-200' : 'text-slate-300 group-hover:text-slate-500'}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {drawerMember && (
          <HistoryDrawer
            member={drawerMember}
            onClose={() => setDrawerMember(null)}
            onBalanceUpdate={(userId, newBalance) => {
              handleBalanceUpdate(userId, newBalance);
              setDrawerMember(prev => prev ? { ...prev, balance: newBalance } : null);
            }}
          />
        )}
      </main>
    </div>
  );
}

// ── Page Router (default export) ──────────────────────────────────────────────
export default function GestionHeuresSup() {
  const { isManager } = useAuth();
  if (!isManager) return <EmployeeOwnView />;
  return <ManagerView />;
}
